type ListenerCallback = EventListenerOrEventListenerObject;
type ListenerOptions = boolean | AddEventListenerOptions | EventListenerOptions;
type ListenerMethod = 'addEventListener' | 'removeEventListener';
type ListenerArgs = [string, ListenerCallback, ListenerOptions | undefined];

const getNativeMethod = (method: ListenerMethod): Function | undefined => {
  const native =
    typeof EventTarget === 'function'
      ? (EventTarget.prototype as unknown as Record<string, unknown>)[method]
      : undefined;

  return typeof native === 'function' ? (native as Function) : undefined;
};

const invoke = (
  method: ListenerMethod,
  target: EventTarget,
  args: ListenerArgs,
): boolean => {
  const native = getNativeMethod(method);
  if (native) {
    try {
      native.apply(target, args);
      return true;
    } catch {
      // A host page that also replaced EventTarget.prototype lands here.
    }
  }

  const patched = (target as unknown as Record<string, unknown>)[method];
  if (typeof patched === 'function') {
    try {
      (patched as (...listenerArgs: ListenerArgs) => void).apply(target, args);
      return true;
    } catch {
      // Nothing left to try — report failure rather than throwing.
    }
  }

  return false;
};

/**
 * Registers `listener` through the native `EventTarget` method instead of
 * whatever `target.addEventListener` currently resolves to.
 *
 * Host pages can replace the listener methods on `document`/`window`: Hosted
 * Payment Pages loads a `zuora.js` that installs legacy IE shims delegating to
 * `attachEvent`/`detachEvent`, which do not exist in modern browsers and throw
 * `TypeError: this.detachEvent is not a function`. Thrown from a React effect
 * cleanup that error unmounts the nav and blanks the page (PBX-663, PBX-730).
 *
 * Returns whether a listener implementation accepted the call.
 */
export function safeAddEventListener(
  target: EventTarget | null | undefined,
  type: string,
  listener: ListenerCallback | null | undefined,
  options?: boolean | AddEventListenerOptions,
): boolean {
  if (!target || !listener) {
    return false;
  }

  return invoke('addEventListener', target, [type, listener, options]);
}

/**
 * Counterpart of {@link safeAddEventListener}. Both must be used together so
 * the same underlying implementation registers and unregisters the listener.
 */
export function safeRemoveEventListener(
  target: EventTarget | null | undefined,
  type: string,
  listener: ListenerCallback | null | undefined,
  options?: boolean | EventListenerOptions,
): boolean {
  if (!target || !listener) {
    return false;
  }

  return invoke('removeEventListener', target, [type, listener, options]);
}
